import React from 'react';
import { ChatMessage } from '@/types/chat';
import MessageContent from './MessageContent';
import ReplyButton from './ReplyButton';
import ThreadPreview from './ThreadPreview';
import ThreadReplies from './ThreadReplies';
import { formatDistanceToNow } from 'date-fns';

interface ThreadedMessageProps {
  message: ChatMessage;
  isExpanded: boolean;
  threadReplies: ChatMessage[];
  onToggleThread: () => void;
  onReplyClick: () => void;
  currentUsername?: string;
  isCurrentUser: boolean;
  isCreator: boolean;
  isHighlighted: boolean;
}

const ThreadedMessage = ({
  message,
  isExpanded,
  threadReplies,
  onToggleThread,
  onReplyClick,
  currentUsername,
  isCurrentUser,
  isCreator,
  isHighlighted
}: ThreadedMessageProps) => {
  const getDisplayName = () => {
    return message.user_profile?.username || 
           message.user_profile?.first_name || 
           'Unknown User';
  };

  const formatTime = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return timestamp;
    }
  };

  const replyingUsers = React.useMemo(() => {
    const uniqueUsers = new Map<string, { username: string }>();
    threadReplies.forEach(reply => {
      const username = reply.user_profile?.username || reply.user_id;
      if (!uniqueUsers.has(username)) {
        uniqueUsers.set(username, { username });
      }
    });
    return Array.from(uniqueUsers.values());
  }, [threadReplies]);

  const latestReplyTime = threadReplies.length > 0
    ? threadReplies[threadReplies.length - 1].created_at
    : undefined;

  return (
    <div className={`group ${isHighlighted ? 'bg-primary/10' : ''}`}>
      {/* Main message */}
      <div
        className={`py-3 px-4 hover:bg-muted/30 transition-colors ${
          isCurrentUser ? 'bg-primary/5' : ''
        }`}
      >
        <div className="flex items-start gap-3">
          {/* Avatar placeholder */}
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-semibold">
            {getDisplayName().charAt(0).toUpperCase()}
          </div>

          {/* Message content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-foreground">
                @{getDisplayName()}
              </span>
              {message.user_profile?.role === 'team-owner' && (
                <span className="text-xs px-1.5 py-0.5 bg-purple-500 text-white rounded font-medium">
                  Team Owner
                </span>
              )}
              {isCreator && (
                <span className="text-xs px-1.5 py-0.5 bg-sports-green text-black rounded font-medium">
                  Creator
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {formatTime(message.created_at)}
              </span>
              <ReplyButton onClick={onReplyClick} />
            </div>
            <MessageContent
              message={message.message}
              currentUsername={currentUsername}
              className="text-sm text-foreground"
            />
          </div>
        </div>
      </div>

      {/* Thread preview (collapsed) */}
      {!isExpanded && (message.reply_count || 0) > 0 && (
        <ThreadPreview
          replyCount={message.reply_count || 0}
          latestReplyTime={latestReplyTime}
          replyingUsers={replyingUsers}
          onExpand={onToggleThread}
        />
      )}

      {/* Thread replies (expanded) */}
      {isExpanded && (
        <ThreadReplies
          replies={threadReplies}
          onCollapse={onToggleThread}
          currentUsername={currentUsername}
        />
      )}
    </div>
  );
};

export default ThreadedMessage;
